/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name whitespace */

import {Window, WindowType} from './window';

import {DockManager} from 'dock-spawn-ts/lib/js/DockManager';
import {PanelContainer} from 'dock-spawn-ts/lib/js/PanelContainer';

import {Logger} from '@/util/logger';

import {Project} from '@/models/project';
import {ProjectElement} from '@/models/project/project.element';
import {ProjectElementType} from '@/models/project/project.element.type';
import {SimulationGroupElement} from '@/models/project/simulation.group.element';
import {PropertiesHolder} from '@/models/properties.holder';
import {IdeService} from '@/services/ide.service';
import {ModalService} from '@/services/modal.service';

import * as $ from 'jquery';
import 'jquery-ui-dist/jquery-ui';

import 'jquery.fancytree/dist/modules/jquery.fancytree.dnd5';
import 'jquery.fancytree/dist/modules/jquery.fancytree.edit';
import 'jquery.fancytree/dist/modules/jquery.fancytree.glyph';
import 'jquery.fancytree/dist/modules/jquery.fancytree.wide';
import 'jquery.fancytree/dist/modules/jquery.fancytree.filter';
import 'jquery.fancytree/dist/modules/jquery.fancytree.multi';
import 'jquery-contextmenu';
import {Subscription} from 'rxjs';
import {BlobElement} from "@/models/project/blob.element";
import {LineSimJobElement} from "@/models/project/line.sim.job.element";

// @ts-ignore
const fancytree = require('jquery.fancytree');

import FancytreeNode = Fancytree.FancytreeNode;
import KeyDownEvent = JQuery.KeyDownEvent;
import {LineSimElement} from "@/models/project/line.sim.element";

/**
 *
 * @class ProjectWindow
 *
 * @brief view panel for the currently loaded project
 *
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class ProjectWindow extends Window {

  /** access to the ide service */
  private                      ide: IdeService           = null;

  /** dialogs service */
  private                   modals: ModalService         = null;

  /** the current simulation that can be simulated */
  private          currentSimulation: SimulationGroupElement = null;

  /** the current blob that can be simulated */
  private              currentBlob: BlobElement          = null;

  /** the observable for project model child additions */
  private              addChildSub: Subscription         = null;

  /** the observable for project model redraws */
  private              redrawChildSub: Subscription      = null;

  /** the observable for element collapse / expand requests */
  private              expandChildSub: Subscription      = null;

  /** the observable for project model child deletions */
  private           deleteChildSub: Subscription         = null;

  /** the current project (if there is one) */
  private                 _project: Project              = null;

  /** the fancy tree object */
  private                    _tree                       = null;

  /** track timeout so we can cancel if we are overlapping */
  private             _renderTimer                       = null;

  /** the currently selected element in the project tree */
  private                _selected: ProjectElement[]     = [];

  private            _rightClicked:   ProjectElement     = null;

  /** standard constructor */
  constructor(
    id: string,
    manager: DockManager,
    ide: IdeService,
    modals: ModalService
  ) {

    super(
      id,
      manager,
      WindowType.Project,
      'Project',
      '<i class="fas fa-project-diagram"></i>'
    );

    this.ide    = ide;
    this.modals = modals;

    Logger.debug('[ProjectWindow] constructing...');

    this.showProject();

  }

  /** close the project view (if its open) */
  public close(implied: boolean = false) {

    Logger.debug('[ProjectWindow] closing ', this.title);

    if (this._tree) {

      $('#projecttree').fancytree('destroy');

      this._tree = null;
    }

    /* we use implied to indicate that the dock spawn manager is doing the closing; not us. */
    super.close(implied);
  }

  /** open the project view (if its closed) */
  public open() : PanelContainer {

    Logger.debug('[ProjectWindow] opening ', this.title);

    const panel: PanelContainer = super.open();

    if(!panel) {

      Logger.error('[ProjectWindow] can not open.');
    }

    Logger.debug('[ProjectWindow] opening...');

    this.showProject();

    return panel;
  }

  /** fetch project of the project view */
  project() : Project {

    return this._project;
  }

  /** set the project being shown */
  setProject(project: Project = null) {

    Logger.debug('[ProjectWindow] setting...', project);

    if (this.expandChildSub) {

      this.expandChildSub.unsubscribe();
      this.expandChildSub = null;
    }

    if (this.redrawChildSub) {

      this.redrawChildSub.unsubscribe();
      this.redrawChildSub = null;
    }

    if (this.addChildSub) {

      this.addChildSub.unsubscribe();
      this.addChildSub = null;
    }

    if (this.deleteChildSub) {

      this.deleteChildSub.unsubscribe();
      this.deleteChildSub = null;
    }

    this._project = project;

    this.showProject();
  }

  /** ensure the project is rendered */
  showProject() {

    const pw = this;

    if (pw._renderTimer) {

      clearTimeout(pw._renderTimer);
    }

    pw._renderTimer = setTimeout(() => {

      pw.host('#solution_window', `
  <div id="solution_window" class="solution-window  window-panel" hidden>
  <div id="projecttree" style="overflow: hidden;"></div>
  </div>`
      );

      Logger.debug('[ProjectWindow] setting up: ', $('#projecttree'), ' for:', pw._project);

      if(pw._project === null) {

        $('#projecttree').html(`<i style="margin: 24px; text-align: center; display: block;">
          Use the <strong style="color: #4B6DAE;">Project</strong> menu above to create or open a project.
        </i>`);

        return ;
      }


      /* - - - - - - setup our tree presentation of the project model - - - - - - - - */

      /* clear any old stuff */

      $('#projecttree').html('');

      if(pw._tree) {


        $('#projecttree').fancytree('destroy');

        pw._tree = null;
      }

      /* setup fresh tree */

      $('#projecttree').fancytree({

        extensions: ['dnd5', 'glyph', 'wide', 'filter', 'multi'],

        selectMode: 2,
        generateIds: true,
        idPrefix: '',

        glyph: {
          preset: 'awesome5',
          map: {}
        },

        imagePath: '/assets/images/',

        icon: '',

        dnd5: {
          autoExpandMS: 1500,
          preventRecursion: true, // Prevent dropping nodes on own descendants
          preventVoidMoves: true, // Prevent dropping nodes 'before self', etc.
          preventNonNodes: false,
          preventForeignNodes: false,

          dragEnter: (node: FancytreeNode, data): boolean => {

            /* remove any existing highlighting */

            /*
            $('.project-drop-accept').removeClass('project-drop-accept');
            $('.project-drop-reject').removeClass('project-drop-reject');
            */

            /* find the presentation node we might be highlighting for drop accept (reject) */

            const titleSpan: JQuery<HTMLElement> = $(`#${node.key}`).find('.fancytree-node >.fancytree-title');

            /* find the project element that might be accepting */

            const pe: ProjectElement = pw._project.child(node.key);

            if (!pe.acceptsDrop(data)) {

              $(titleSpan).addClass('project-drop-reject');

              return true;
            }

            $(titleSpan).addClass('project-drop-accept');

            return true;
          },

          dragOver: (node: FancytreeNode, data): void => {

            /* find the presentation node we might be highlighting for drop accept (reject) */

            const titleSpan: JQuery<HTMLElement> = $(`#${node.key}`).find('.fancytree-node >.fancytree-title');

            /* find the project element that might be accepting */

            const pe: ProjectElement = pw._project.child(node.key);

            if (!pe.acceptsDrop(data)) {

              $(titleSpan).addClass('project-drop-reject');

              return ;
            }

            $(titleSpan).addClass('project-drop-accept');

            data.dropEffect = data.dropEffectSuggested;

          },

          dragEnd: (node: FancytreeNode, data): void => {

            /* remove any existing highlighting */

            $('.project-drop-accept').removeClass('project-drop-accept');
            $('.project-drop-reject').removeClass('project-drop-reject');

          },

          dragLeave: (node: FancytreeNode, data): void => {

            /* remove any existing highlighting */

            $('.project-drop-accept').removeClass('project-drop-accept');
            $('.project-drop-reject').removeClass('project-drop-reject');

          },

          dragDrop: (node: FancytreeNode, data): void => {

            /* remove any existing highlighting */

            $('.project-drop-accept').removeClass('project-drop-accept');
            $('.project-drop-reject').removeClass('project-drop-reject');

            /* find the project element that might be accepting */

            const pe: ProjectElement = pw._project.child(node.key);

            if (!pe.acceptsDrop(data.dataTransfer)) {

              /* this one doesn't accept drops anyways */

              return ;
            }

            data.originalEvent.preventDefault();

            /* let the element do whatever it does with dropped data */

            try {

              pe.acceptDrop(data);

              node.setExpanded();

            } catch (error) {

              Logger.error('[ProjectWindow] can not do drop: ',  error);
            }
          }

        },

        click: (event, data): boolean => {

          const n: FancytreeNode   = data.node;
          const el: ProjectElement = pw._project.child(n.key);

          const properties: PropertiesHolder = el.properties();

          pw.ide.focus(properties);

          /* update what the current simulation focus is */

          pw.setSimulation(el);

          return true;
        },

        collapse: (event, data) => {

          /* what element is involved? */

          const n: FancytreeNode   = data.node;
          const el: ProjectElement = pw._project.child(n.key);

          el._expanded = false;

          /* remember new tree structure */

          if (el.canSkipSave()) {

            /* if the node was marked as skippable, because a batch process will
             * save the project at its end anyways, then we don't do the save, but
             * we clear the flag, so that we don't keep skipping when the user
             * later wants to manually expand or collapse.
             */

            Logger.debug('[ProjectWindow] skipping save (collapse): ' + el._label);

            el.skipSave(false);

          } else {

            pw.save();
          }
        },

        expand: (event, data) => {

          /* what element is involved? */

          const n: FancytreeNode   = data.node;
          const el: ProjectElement = pw._project.child(n.key);

          /* remember new tree structure */

          el._expanded = true;

          if (el.canSkipSave()) {

            /* if the node was marked as skippable, because a batch process will
             * save the project at its end anyways, then we don't do the save, but
             * we clear the flag, so that we don't keep skipping when the user
             * later wants to manually expand or collapse.
             */

            Logger.debug('[ProjectWindow] skipping save (expand): ' + el._label);

            el.skipSave(false);

          } else {

            /* trigger a save */

            pw.save();
          }
        },

        source: (event, data) => {

          const rendering:any = [pw.renderNode(event, data)];
          return rendering;
        }
      });

      $('#projecttree').on('keydown', (e:KeyDownEvent) => {

        if (e.key == 'Delete') {

          pw.deleteSelected();

          return false;

        } else if(e.ctrlKey && e.key.toLowerCase() == 'c') {

          pw.copyToClipboard();

          return false;

        } else if(e.ctrlKey && e.key.toLowerCase() == 'v') {

          pw.pasteFromClipboard();

          return false;

        } else if(e.ctrlKey && e.key.toLowerCase() == 'x') {

          pw.moveFromClipboard();

          return false;
        }

      });

      // noinspection JSJQueryEfficiency
      pw._tree = $('#projecttree').fancytree('getTree');

      /* - - - - - watch for changes to the model tree, update presentation */

      pw.expandChildSub = pw._project.onExpandChild().subscribe((request: [ProjectElement[], boolean]): void => {

        if (request === null) {

          return ;
        }

        const expand: boolean  = request[1];

        request[0].forEach((child: ProjectElement): void => {

          Logger.debug(`[ProjectWindow] onExpandChild - expanding/collapsing (${expand}): `, child);

          const node: FancytreeNode = pw._tree.getNodeByKey(child._id);

          child._expanded = expand;

          node.setExpanded(expand);

          // @ts-ignore
          const      icon: any    = child?._icon;
          const iconClass: string = child?._iconClass;
          let   nodeClass: string = child?._nodeClass;
          const   biClass: string = child?.busyIconClass;
          const   bnClass: string = child?.busyNodeClass;

          if (icon) {

            // @ts-ignore
            node.icon = icon;
          }

          // @ts-ignore
          node.iconClass    = iconClass;

          /* do we have an override for the icon styling? */

          if((biClass !== null) && (biClass !== '')) {

            // @ts-ignore
            node.iconClass  = biClass;
          }

          if((bnClass !== null) && (bnClass !== '')) {

            // @ts-ignore
            nodeClass       = bnClass;
          }


          node.extraClasses = nodeClass;

          node.renderTitle();

          node.setTitle(child._label);

          node.getParent().sortChildren();

          node.renderTitle();

        });

      });

      pw.redrawChildSub = pw._project.onRedrawChild().subscribe((children) => {

        children.forEach((child: ProjectElement): void => {

          const node: FancytreeNode = pw._tree.getNodeByKey(child._id);

          if (!node) {

            /* this child isn't in the tree yet */

            return ;
          }

          Logger.debug(`[ProjectWindow] onRedrawChild - redrawing: `, child);

          // @ts-ignore
          const      icon: any    = child?._icon;
          const iconClass: string = child?._iconClass;
          let   nodeClass: string = child?._nodeClass;
          const   biClass: string = child?.busyIconClass;
          const   bnClass: string = child?.busyNodeClass;

          if (icon) {

            // @ts-ignore
            node.icon = icon;
          }

          // @ts-ignore
          node.iconClass    = iconClass;

          /* do we have an override for the icon styling? */

          if((biClass !== null) && (biClass !== '')) {

            // @ts-ignore
            node.iconClass  = biClass;
          }

          if((bnClass !== null) && (bnClass !== '')) {

            // @ts-ignore
            nodeClass       = bnClass;
          }


          node.extraClasses = nodeClass;

          node.setExpanded(child._expanded);

          node.renderTitle();

          node.setTitle(child._label);

          node.getParent().sortChildren();

          node.renderTitle();
        });

      });

      pw.addChildSub = pw._project.onAddChild().subscribe(pair => {

        if (!pair) {

          return ;
        }

        const c: ProjectElement = pair.child;
        const p: ProjectElement = pair.parent;

        /* find the parent in FancyTree */

        let   node: FancytreeNode = pw._tree.getNodeByKey(p._id);
        let before: FancytreeNode = null;

        if (!node) {

          /* if hte parent's upwards link has been set...then try to auto-insert the parent first... */

          if (p._parent) {

            Logger.debug('[ProjectWindow] addChildSub - auto inserting parent...');

            const pp: ProjectElement = p._parent;
            const pc: ProjectElement = p;
            const pn: FancytreeNode = pw._tree.getNodeByKey(pp._id);

            /* before we can add it, we have to know where it goes in the parent */

            for (let i=0; i<pp._children.length; i++) {

              if ((i+1) >= pp._children.length) {

                break;
              }

              if (pp._children[i]._id !== pc._id) {

                continue;
              }

              const nextKey: string = pp._children[i+1]._id;
              before = pw._tree.getNodeByKey(nextKey);

              break;
            }

            /* render/add the children */

            pn.setExpanded(true);

            pp._expanded = true;

            pn.addChildren(pc.render(null, null), before);

          } else {

            Logger.error('[ProjectWindow] addChildSub parent has not been added to the tree yet!');
            return;
          }

          Logger.debug('[ProjectWindow] addChildSub - auto-insertion completed.');

          /* the node fetch on the parent should work now */

          before = null;
          node   = pw._tree.getNodeByKey(p._id);

        } /* end of parent auto-add */

        /* before we can add it, we have to know where it goes in the parent */

        for (let i=0; i<p._children.length; i++) {

          if ((i+1) >= p._children.length) {

            break;
          }

          if (p._children[i]._id !== c._id) {

            continue;
          }

          const nextKey: string = p._children[i+1]._id;
          before = pw._tree.getNodeByKey(nextKey);

          break;
        }

        /* render/add the children */

        node.setExpanded(true);

        p._expanded = true;

        node.addChildren(c.render(null, null), before);

      });

      pw.deleteChildSub = pw._project.onDeleteChild().subscribe((children: ProjectElement[]) => {

        if(!children || children.length === 0) {

          /* nothing to do */

          return ;
        }

        children.forEach((child: ProjectElement) => {

          const node: FancytreeNode  = pw._tree.getNodeByKey(child._id);

          if(!node) {

            return ;
          }

          /* remove from presentation */

          node.remove();

        });

      });

      /* - - - - - setup our project manager right click menu - - - - - - - - */

      $.contextMenu( 'destroy' );

      // @ts-ignore be sure to use v2.6.4 of jquery-contextmenu
      $.contextMenu({

        selector: '.fancytree-container',

        autoHide: true,
        reposition: false,
        hideOnSecondTrigger: true,
        zIndex: 100000,

        className: 'data-title',
        events: {

          show: (options) => {

            /*
             * in full screen mode we can only see children of the thing that is full screen, by default context
             * menu is a child of 'body'.  We can't just invoke contextMenu() on the right <div>, because it calls
             * the other contextmenu function already in jQuery.
             *
             */

         //   $('.data-title').detach().appendTo('.fancytree-container');

            $('.data-title').attr('data-menutitle', pw._selected[0]._label);
          }
        },

        build: ($trigger, e: any) => {

          if (typeof e.originalEvent === 'undefined') {

            return false;
          }

          /* select the tree node they right clicked on */

          const domNode: any        = $(e.originalEvent.target).closest('li');
          const id: string          = $(domNode).attr('id');

          if (!domNode || !id) {

            return false;
          }

          pw._rightClicked = pw._project.child(id);

          /* update what the current simulation focus is */

          pw.setSimulation(pw._rightClicked);

          const node:        FancytreeNode = pw._tree.getNodeByKey(id);
          const selection: FancytreeNode[] = pw._tree.getSelectedNodes();

          if((selection.length === 0)||(!selection.find(x => x.key === node.key))) {

            /* implied selection, outside of current selection */

            for(const item of selection) {

              item.setSelected(false);
              item.setActive(false);
            }

            node.setSelected(true);
            node.setActive(true);

          }

          pw._selected = [];

          for (const item of pw._tree.getSelectedNodes()) {

            pw._selected.push(pw._project.child(item.key));
          }

          return {

            callback: (key, options) => {

              /* do the action on the current selection */

              pw.selectionAction(key);

            },

            items: pw.selectContextAction()
          };
        }

      });

    }, 50);

  }

  public selectedResult(): LineSimJobElement|LineSimElement {

    if (!this._tree) {

      return null;
    }

    /* gather the current selection ... */

    const selection: FancytreeNode[] = this._tree.getSelectedNodes();

    if (selection.length === 0) {

      /* nothing to do */

      return null;
    }

    this._selected = [];

    selection.forEach((node: FancytreeNode) => {

      this._selected.push(this._project.child(node.key));

    });

    /* take teh first one in the selection ... */

    let result: LineSimJobElement|LineSimElement = null;
    let     el: ProjectElement    = this._selected[0];

    /* look upwards until we find a Blob */

    while (el !== null) {

      if (el._type === ProjectElementType.LineSimResult) {

          result = el as LineSimJobElement;
          break;

      }

      if (el._type === ProjectElementType.LineSimResultCalculated) {

        result = el as LineSimElement;
        break;
      }

      el = el._parent;
    }

    /* pass back the match */

    return result;
  }

  /** helper to get the selected scenario element (if there is one) */
  public selectedScenario(): BlobElement {

    if (!this._tree) {

      return ;
    }

    /* gather the current selection ... */

    const selection: FancytreeNode[] = this._tree.getSelectedNodes();

    if (selection.length === 0) {

      /* nothing to do */

      return ;
    }

    this._selected = [];

    selection.forEach((node: FancytreeNode) => {

      this._selected.push(this._project.child(node.key));

    });

    /* take teh first one in the selection ... */

    let result: BlobElement    = null;
    let     el: ProjectElement = this._selected[0];

    /* look upwards until we find a Blob */

    while (el !== null) {

      if (el._type === ProjectElementType.Blob) {

        /* if its parent is Input or Dimension, its likely a scenario */

        if ((el._parent._type === ProjectElementType.Inputs) ||
            (el._parent._type === ProjectElementType.Dimension)) {

          result = el as BlobElement;

          break;
        }
      }

      el = el._parent;
    }

    /* pass back the match */

    return result;
  }

  /** helper to delete multi-selection */
  public deleteSelected() {

    Logger.debug('[ProjectWindow] deleting selection...');

    /* gather the selection */

    const selection: FancytreeNode[] = this._tree.getSelectedNodes();

    if (selection.length === 0) {

      /* nothing to do */

      return ;
    }

    this._selected = [];

    selection.forEach((node: FancytreeNode) => {

      this._selected.push(this._project.child(node.key));

    });

    /* do the delete */

    this._project.root().deleteElements(this._selected);
  }

  /** helper to CTRL-C multi-selection */
  public copyToClipboard() {

    Logger.debug('[ProjectWindow] copying selection...');



  }

  /** helper to CTRL-V multi-selection */
  public pasteFromClipboard() {

    Logger.debug('[ProjectWindow] pasting selection...');



  }

  /** helper to CTRL-X multi-selection */
  public moveFromClipboard() {

    Logger.debug('[ProjectWindow] moving selection...');



  }

  /** when they click or right click, we update what the current simulation is (that can be simulated) */
  setSimulation(child: ProjectElement) {

    this.currentSimulation = null;

    /* look upwards from here, as soon as we find an SimulationGroup element we have it */

    let parent: ProjectElement = child;

    while (parent !== null) {

      if (parent instanceof BlobElement) {

        this.currentBlob = parent as BlobElement;

        /* publish it */

        this.ide.setCurrentBlob(this.currentBlob);

        break;
      }

      if (parent instanceof SimulationGroupElement) {

        this.currentSimulation = parent as SimulationGroupElement;

        /* publish it */

        this.ide.setCurrentSimulation(this.currentSimulation);

        break;
      }

      parent = parent._parent;
    }


  }

  /** walk the selected elements to get the applicable actions */
  selectContextAction() {

    let actionSet: any = {};

    /* add node specific actions */

    actionSet = this._rightClicked.actions(actionSet);

    /* add the allowed cut/copy/paste operations */

    /*
    actionSet.sepCopyPaste = '---------';

    actionSet.cut = {
      name: 'Cut',
      icon: 'fas fa-cut',
      disabled: true
    };

    actionSet.copy = {
      name: 'Copy',
      icon: 'fas fa-copy',
      disabled: false
    };

    actionSet.paste = {
      name: 'Paste',
      icon: 'fas fa-paste',
      disabled: false
    };

     */

    /* add general operations */



    /* all done */

    return actionSet;
  }

  /** apply the chosen context action to the currently selected elements */
  selectionAction(key: string) {

    if(this._rightClicked.acceptsAction(key)) {

      this._rightClicked.action(key, this._selected);

      return ;
    }

    /* do actions not specific to an element (works on selection or project) */

    Logger.debug('[ProjectWindow] context action: ', key);



  }

  /** helper to render Project into FancyTree */
  renderNode(event, data) {

    return this._project.render(event, data);
  }
}
